<template>
  <Transition name="drawer-slide">
    <div
      v-if="isOpen"
      class="WalkHistoryReveal"
      @click.stop
      @click.self="emit('close')"
      @touchend.stop
      @touchend.self.prevent="emit('close')"
    >
      <div ref="drawerRef" class="reveal-drawer" @click.stop @touchend.stop>
        <div class="drawer-top-bar">
          <div class="drawer-more">
            <GamMore
              :size="GamButtonSize.X_SMALL"
              :items="moreItems"
              :position="GamMorePosition.RIGHT_BELOW"
              :variant="GamButtonVariant.STEKLO"
            />
          </div>
          <div class="drawer-close" @click="emit('close')" @touchend.prevent="emit('close')">✕</div>
          <div class="drawer-header">
            <span v-if="formattedDate" class="drawer-date">{{ formattedDate }}</span>
          </div>
        </div>
        <div class="drawer-scroll-content" :class="{ 'no-footer': !canViewOnMap }">
          <WalkRevealContent :summary="summary" is-history />
        </div>
        <div v-if="canViewOnMap" class="drawer-footer">
          <span class="drawer-cta view-map-cta" @click="handleViewOnMap">{{
            localize('walk.reveal.view_on_map')
          }}</span>
        </div>
      </div>
    </div>
  </Transition>

  <Teleport to="body">
    <GamConfirmModal
      v-if="showDeleteConfirm"
      :title="localize('walk.session.delete.title')"
      :keep-label="localize('walk.session.delete.keep')"
      :destructive-label="localize('walk.session.delete.confirm')"
      @close="showDeleteConfirm = false"
      @keep="showDeleteConfirm = false"
      @confirm="handleDeleteSession"
    />
  </Teleport>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { shareOrDownloadFile } from '@/core/helpers/file-share.helper';
import { platform } from '@/core/platform';
import { useMapPuzzleStore } from '@/stores/mapPuzzle.store';
import { useWalkStore } from '@/stores/walk.store';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import type { GamMoreItem } from '@/views/composables/models/components/GamMore';
import { computed, ref, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { buildWalkOverlayPng } from '../utils/walk-overlay';
import WalkRevealContent from './WalkRevealContent.vue';

const props = defineProps<{
  isOpen: boolean;
  sessionDate?: string;
}>();

const emit = defineEmits<{
  close: [];
  deleted: [];
}>();

const router = useRouter();
const store = useWalkStore();
const summary = computed(() => store.historySummary);
const drawerRef = ref<HTMLElement | null>(null);
const showDeleteConfirm = ref(false);

const canViewOnMap = computed(() => {
  if (platform.isOfflineCapable && !navigator.onLine) return false;
  if (!summary.value) return false;
  if (summary.value.sessionId !== store.lastRevealedSessionId) return false;
  const puzzleStore = useMapPuzzleStore();
  return puzzleStore.markers.length > 0;
});

useDrawerGesture(drawerRef, () => emit('close'));

const moreItems = computed<GamMoreItem[]>(() => [
  {
    label: 'walk.session.export.action',
    onClick: () => {
      void handleExportOverlay();
    },
  },
  {
    label: 'walk.session.delete.action',
    onClick: () => {
      showDeleteConfirm.value = true;
    },
  },
]);

/* Rendered up front: iOS blocks the share sheet once the tap's user activation
   has been spent waiting for the canvas. */
const overlayPng = shallowRef<Promise<Blob | null> | null>(null);

watch(
  [() => props.isOpen, summary],
  ([isOpen, current]) => {
    overlayPng.value = isOpen && current ? buildWalkOverlayPng(current) : null;
  },
  { immediate: true },
);

function overlayFilename(): string {
  return `walk-${props.sessionDate || new Date().toLocaleDateString('en-CA')}.png`;
}

async function handleExportOverlay() {
  const current = summary.value;
  if (!current) return;

  const pending = overlayPng.value ?? buildWalkOverlayPng(current);
  overlayPng.value = pending;

  const blob = await pending;
  if (!blob) {
    useToast().error(localize('walk.session.export.error'), { id: 'walk-overlay-export' });
    return;
  }

  const result = await shareOrDownloadFile(blob, overlayFilename(), localize('walk.session.export.action'));
  if (result === 'failed') {
    useToast().error(localize('walk.session.export.error'), { id: 'walk-overlay-export' });
  }
}

async function handleDeleteSession() {
  const sessionId = summary.value?.sessionId;
  if (!sessionId) return;

  const success = await store.deleteSession(sessionId);
  showDeleteConfirm.value = false;
  if (success) {
    emit('close');
    emit('deleted');
  }
}

function handleViewOnMap() {
  store.requestShowWalkMarkers();
  emit('close');
  router.replace({ name: RouterNameType.LOCATIONS, query: { fit_markers: 'true' } });
}

const formattedDate = computed(() => {
  if (!props.sessionDate) return '';
  const now = new Date();
  const todayKey = now.toLocaleDateString('en-CA');
  const yesterdayKey = new Date(now.getTime() - 86400000).toLocaleDateString('en-CA');

  if (props.sessionDate === todayKey) return 'TODAY';
  if (props.sessionDate === yesterdayKey) return 'YESTERDAY';

  const date = new Date(props.sessionDate + 'T00:00:00');
  const weekday = date.toLocaleDateString('en', { weekday: 'short' }).slice(0, 2).toUpperCase();
  const day = date.getDate();
  const month = date.toLocaleDateString('en', { month: 'short' });
  return `${weekday} ${day} ${month}`;
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.WalkHistoryReveal {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.reveal-drawer {
  position: relative;
  width: 100%;
  max-height: calc(100vh - env(safe-area-inset-top) - 1rem);
  max-height: calc(100dvh - env(safe-area-inset-top) - 1rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(20deg, oklch(0.18 0 0) 0%, oklch(0.22 0 0) 50%, oklch(0.18 0 0) 100%);
  border-radius: 1.5rem 1.5rem 0 0;
  border: 1px solid var(--color-white-10);
  border-bottom: none;
  will-change: transform;
}

.drawer-top-bar {
  position: relative;
  flex-shrink: 0;
}

.drawer-scroll-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.no-footer {
    padding-bottom: var(--safe-bottom-offset);
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--color-white-10);
}

.drawer-date {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-white-70);
}

.drawer-more {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 1.5rem;
  z-index: 1;
}

.drawer-close {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 1.5rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
  font-size: 16px;
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white);
  }

  @include press($transform: translateY(-50%));
}

.drawer-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem max(1rem, var(--safe-bottom-offset));
  border-top: 1px solid var(--color-white-10);
}

.drawer-cta {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  height: 3rem;
  padding: 0 20px;
  border-radius: 2rem;
  transition: all 200ms linear;

  @include press;
}

.view-map-cta {
  color: var(--color-white-70);
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;

  .reveal-drawer {
    transition: transform 200ms ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .reveal-drawer {
    transform: translateY(100%);
  }
}
</style>
